import React, { useCallback, useEffect, useState } from 'react'
import draftToHtml from 'draftjs-to-html'
import htmlToDraft from 'html-to-draftjs'
import { EditorState, ContentState, convertToRaw } from 'draft-js'
import { Editor } from 'react-draft-wysiwyg'
import './theme.css'
import { useTranslation } from 'react-i18next'
import { useAuth } from '~/routes/components/AuthProvider'

type Props = {
  value?: string,
  onChange?: (value: string) => void,
  isReadOnly?: boolean;
  useDefaultValue?: boolean;
  hideToolbar?: boolean;
  maxLength?: number
}

const createEditorState = value => {
  if (value) {
    const contentBlock = htmlToDraft(value)
    if (contentBlock) {
      const contentState = ContentState.createFromBlockArray(contentBlock.contentBlocks)
      return EditorState.createWithContent(contentState)
    }
  }
  return EditorState.createEmpty()
}

export default function WYSIWYGEditor(props: Props) {
  const [editorState, setEditorState] = useState<EditorState>(createEditorState(props.value))
  const { t } = useTranslation()
  const onChange = useCallback(props.onChange, [])
  const { systemData } = useAuth()

  const languageCode = systemData?.languages.find(
    language => language.id === systemData.logged.languageId)?.code

  function onEditorStateChange(editorState: EditorState) {
    setEditorState(editorState)
    if (onChange) {
      onChange(draftToHtml(convertToRaw(editorState.getCurrentContent())))
    }
  }

  useEffect(() => {
    setEditorState(createEditorState(props.value))
  }, [])

  function getCorrectLanguageCode(languageCode) {
    switch (languageCode) {
      case 'gb':
        return 'en'
      case 'fr_BE':
        return 'fr'
      case 'nl_BE':
        return 'nl'
      default:
        return languageCode
    }
  }

  const maxLength = props.maxLength;
  const plainText = editorState.getCurrentContent().getPlainText()?.trim();

  const toolbarOptions = {
    options: ['inline', 'link', 'textAlign'],
    textAlign: {
      inDropdown: false,
      options: ['left', 'center', 'right', 'justify']
    },
    inline: {
      options: ['bold', 'italic', 'underline', 'strikethrough']
    },
    link: {
      options: ['link']
    }
  };

  const correctLanguageCode = getCorrectLanguageCode(languageCode)

  return <>
    <Editor
      toolbar={toolbarOptions}
      placeholder={t('Enter text')}
      editorState={editorState}
      wrapperClassName='wysiwyg-editor-wrapper'
      editorClassName='wysiwyg-editor'
      toolbarClassName={`${props.hideToolbar ? 'hide-toolbar' : null}`}
      onEditorStateChange={onEditorStateChange}
      readOnly={props.isReadOnly}
      defaultEditorState={props.useDefaultValue ? props.value : null}
      localization={{
        locale: correctLanguageCode
      }}
    />
    {props.maxLength && <div>{`${plainText?.length || 0} / ${maxLength}`}</div>}
  </>
}
